<template>
  <v-card :class="compact && 'elevation-0 pa-0'">
    <v-card-title
      v-if="!(donationCases.length > 1)"
      :class="compact && 'pl-0 pt-0'"
      class="text-center mx-auto"
      data-cy="caseview-title"
    >
      <span class="donation-flow__step-title">
        {{ t('deceasedDetails.title') }}
      </span>
    </v-card-title>
    <!-- when there are multiple cases, the dropdown is shown and we want a smaller explanatory subtitle instead -->
    <div
      v-else
      class="caseview-subtitle mb-8"
      data-cy="caseview-subtitle"
    >
      {{ t('funeralCase.findPerson') || 'Find the person you want to honor with your donation' }}
    </div>
    <v-card-text :class="compact && 'pa-0'">
      <v-form
        ref="form"
        v-model="valid"
        :lazy-validation="true"
        data-cy="caseview-form"
        style="width: 100%"
      >
        <v-select
          v-if="donationCases.length > 0"
          :key="selectKey"
          v-model="donationCase"
          :items="donationCases"
          item-title="caseInfo.name"
          :placeholder="t('funeralCase.chooseFuneral')"
          persistent-placeholder
          density="comfortable"
          class="compact-select mt-4"
          return-object
          data-cy="case-view-dropdown"
          :rules="[rules.requiredSelection(t('funeralCase.chooseFuneral'))]"
          required
          :menu-props="{ offset: 10 }"
        >
          <template #item="{ item, props }" >
            <v-list-item
              v-if="item.raw && item.raw.caseInfo"
              v-bind="{ ...props, class: (props.class || '') + ' case-dropdown-item' }"
              :title="item.raw.caseInfo.name"
            >
              <template #title>
                <v-list-item-subtitle class="case-item-subtitle case-item-name" >{{ item.raw.caseInfo.name }}</v-list-item-subtitle>
              </template>
              <template #subtitle>
                <v-list-item-subtitle v-if="getBirthEvent(item.raw)?.date" class="case-item-subtitle">
                  {{ t('funeralCase.dateOfBirth', { date: getBirthEvent(item.raw)?.date || '' }) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="getFuneralEvent(item.raw)?.venue" class="case-item-subtitle">
                  {{ t('funeralCase.venue', { venue: getFuneralEvent(item.raw)?.venue || '' }) }}
                </v-list-item-subtitle>
              </template>
            </v-list-item>
            <v-divider />
          </template>
        </v-select>
        <donation-case-form-component
          v-else
          :rules="rules"
          :value="donationCase as DonationCase"
          @update:value="donationCase = $event"
        />
      </v-form>
    </v-card-text>
    <donation-flow-buttons
      :show-previous="requestHasOrganizationGroup || showPrev"
      previous-data-cy="case-view-back-btn"
      next-data-cy="case-view-next-btn"
      next-id="caseViewNextButton"
      @previous-step="previousStep()"
      @next-step="next"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { CaseInfo } from '@/interfaces/donation/donationcase/caseInfo';
import { CaseEvent, CaseEventType } from '@/interfaces/donation/donationcase/caseEvent';
import { DonationCase } from '@/interfaces/donation/donationcase/donationCase';
import { Donation } from '@/interfaces/donation/donation';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import DonationCaseFormComponent from '@/components/donationcase/DonationCaseFormComponent.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import { RequestSession } from '@/interfaces/requestSession';
import { isMemoryGift } from '@/classes/DonationType';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { useStepperFlow } from '@/composables/useStepperFlow';
import { useValidationRules } from '@/composables/useValidationRules';
import { useLabel } from '@/composables/useLabel';
import store from '@/store';
import { scrollToFirstInvalid } from '@/utils/formHelpers';
import matomo from '@/services/matomoService';

export default defineComponent({
  components: {
    DonationCaseFormComponent,
    DonationFlowButtons,
  },
  // replaced StepperFlowMixin with composable useStepperFlow
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    compact: Boolean,
    requestHasExternalAgent: {
      type: Boolean,
      default: false,
    },
    requestHasOrganizationGroup: {
      type: Boolean,
      default: false,
    },
    showPrev: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'previousStep', 'nextStep', 'addStep', 'removeStep'],
  setup(props, { emit }) {
    const form = ref();

    const valid = ref(false);
    const skipStep = ref(false);
    const donationCases = ref<DonationCase[]>([]);
    // when multiple choices are present we clear this to null later; allow null here
    const donationCase = ref<DonationCase | null>(
      (props.modelValue as DonationCase) ?? {
        caseInfo: {
          additionalInfo: '',
          events: [
            {
              date: '',
              place: '',
              time: '',
              venue: '',
              type: CaseEventType.FUNERAL,
            },
            {
              date: '',
              place: '',
              time: '',
              venue: '',
              type: CaseEventType.BIRTH,
            },
            {
              date: '',
              place: '',
              time: '',
              venue: '',
              type: CaseEventType.DEATH,
            },
          ] as CaseEvent[],
          name: '',
          intermediary: null,
          recipient: null,
        } as CaseInfo,
        intermediary: null,
      }
    );

    const { t } = useLabel();
    const { rules } = useValidationRules(t);

    const { getDonationStepFlowVersion } = useStepperFlow();

    const steps = computed((): typeof stepV1 | typeof stepV2 => {
      return getDonationStepFlowVersion() == DonationGuideVersion.V1 ? stepV1 : stepV2;
    });
    const donation = computed((): Donation => store.state.donation!);
    const currentStep = computed(() => store.state.currentStep);
    const requestSession = computed((): RequestSession => store.state.requestSession!);

    watch(currentStep, (step) => {
      if (step === steps.value.CASE) {
        matomo.trackEvent('Commemorative Gift', 'Viewed', 'Deceased Details Screen');
      }
    });

    watch(donationCase, (newVal, oldVal) => {
      if (
        newVal &&
        newVal.recipient?.isFuneralHome &&
        newVal.id !== oldVal?.id
      ) {
        matomo.trackEvent('Commemorative Gift', 'Selected', 'Funeral Home');
      }
    });

    watch(
      () => donationCase.value?.caseInfo.events?.[1]?.date,
      (newBirth) => {
        const death = donationCase.value?.caseInfo.events?.[2]?.date;
        if (newBirth && death && newBirth > death) {
          matomo.trackEvent('Commemorative Gift', 'Validation Error', 'Inconsistent Dates');
        }
      }
    );

    watch(
      () => donationCase.value?.caseInfo.events?.[2]?.date,
      (newDeath) => {
        const birth = donationCase.value?.caseInfo.events?.[1]?.date;
        if (birth && newDeath && birth > newDeath) {
          matomo.trackEvent('Commemorative Gift', 'Validation Error', 'Inconsistent Dates');
        }
      }
    );

    watch(
      () => donationCase.value?.recipient,
      (newRecipient, oldRecipient) => {
        if (
          newRecipient &&
          newRecipient.isFuneralHome &&
          newRecipient !== oldRecipient
        ) {
          matomo.trackEvent('Commemorative Gift', 'Selected', 'Funeral Home');
        }
      }
    );
    const isMemoryGiftComputed = computed(
      (): boolean =>
        donation.value?.donationType?.type && isMemoryGift(donation.value.donationType.type)
    );

    const selectKey = computed(() => donationCase.value?.id || '');

    const handleDonationCasesFromRequestSession = () => {
      if (requestSession.value.donationCases && requestSession.value.donationCases.length > 0) {
        donationCases.value = requestSession.value.donationCases.filter((dc) => dc && dc.caseInfo);
        // only auto-select when exactly one case is available
        if (donationCases.value.length === 1) {
          donationCase.value = donationCases.value[0];
          if (props.requestHasExternalAgent) {
            donationCase.value.caseInfo.additionalInfo = '';
          }
        } else {
          donationCase.value = props.requestHasExternalAgent ? null : donationCases.value[0];
        }
        skipStep.value = donationCases.value.length == 1;

        // log if user has landed on the multi-case dropdown page
        if (donationCases.value.length > 1 && currentStep.value === steps.value.CASE) {
          matomo.trackEvent('Commemorative Gift', 'Viewed', 'Select Death Case Page');
          // report number of options for analytics
          const count = donationCases.value.length;
          const nameLabel = count === 0 ? 'Zero Results' : `${count} Results`;
          matomo.trackEvent('Donation Case Selection', 'Result Shown', nameLabel);
        }
      }
    };

    const previousStep = () => {
      if (donationCases.value.length <= 1) {
        matomo.trackEvent('Commemorative Gift', 'Clicked', 'Previous');
      }
      emit('previousStep');
    };

    const next = () => {
      matomo.trackEvent('Commemorative Gift', 'Clicked', 'Next');
      if (!skipStep.value) {
        if (donationCases.value.length > 1) {
          matomo.trackEvent('Donation Case Selection', 'Continued', 'Donation Flow Started');
          form.value?.validate();
          if (!valid.value) {
            matomo.trackEvent('Commemorative Gift', 'Validation Error', 'Missing Required Fields');
            try {
              scrollToFirstInvalid(form.value, null);
            } catch {
              // no-op
            }
            return;
          }
          if (!donationCase.value) {
            return;
          }
        } else {
          if (!donationCase.value) {
            return;
          }

          form.value?.validate();
          if (!valid.value) {
            matomo.trackEvent('Commemorative Gift', 'Validation Error', 'Missing Required Fields');
            try {
              scrollToFirstInvalid(form.value, null);
            } catch {
              // no-op
            }
            return;
          }

          // Manual validation for required fields
          let isValid = true;
          let missingFields: string[] = [];

          const caseInfo = donationCase.value.caseInfo;
          if (!caseInfo.name || !caseInfo.name.trim()) {
            isValid = false;
            missingFields.push(t('deceasedDetails.form.name.label'));
          }

          if (
            !caseInfo.events[0].place ||
            !caseInfo.events[0].place.trim()
          ) {
            isValid = false;
            missingFields.push(t('deceasedDetails.form.funeralPlace.label'));
          }

          if (!isValid) {
            try {
              scrollToFirstInvalid(form.value, null);
            } catch {
              // no-op
            }
            return;
          }
        }
      }
      const donationVal: Donation = donation.value;
      if (donationCases.value.length > 1) {
        matomo.trackEvent('Commemorative Gift', 'Continued', 'Donation Flow Started');
      }

      donationVal.donationCase = donationCase.value as DonationCase;
      store.commit('updateDonation', donationVal);
      emit('nextStep');
    };

    watch(
      currentStep,
      (newVal, oldVal) => {
        // Safety check: ensure donation is initialized
        if (!donation.value?.donationType) {
          return;
        }

        if (newVal === steps.value.CASE) {
          handleDonationCasesFromRequestSession();
        }
        // if skipStep is true a DonationCase has been set from the RequestSession
        if (newVal === steps.value.CASE && skipStep.value) {
          emit('removeStep', steps.value.CASE);
          if (oldVal === steps.value.CASE + 1) {
            emit('previousStep');
          } else {
            next();
          }
        }
        if (newVal === steps.value.CASE && !isMemoryGiftComputed.value) {
          emit('removeStep', steps.value.CASE);
          if (oldVal === steps.value.CASE + 1) {
            emit('previousStep');
          } else {
            emit('nextStep');
          }
        } else {
          emit('addStep', steps.value.CASE);
        }
      },
      { immediate: true }
    );

    // Re-evaluate step inclusion when donation type changes
    watch(
      () => donation.value?.donationType,
      () => {
        // Safety check: ensure donation type exists
        if (!donation.value?.donationType) {
          return;
        }

        const currentVal = currentStep.value;
        if (currentVal === steps.value.CASE) {
          if (!isMemoryGiftComputed.value) {
            emit('removeStep', steps.value.CASE);
          } else {
            emit('addStep', steps.value.CASE);
          }
        }
      }
    );
    watch(
      donationCase,
      (newVal, oldVal) => {
        if (
          donationCases.value.length > 1 &&
          oldVal &&
          newVal &&
          newVal.id !== oldVal.id
        ) {
          matomo.trackEvent('Donation Case Selection', 'Selected', 'Case Selected');
        }
      }
    );
    const getBirthEvent = (donationCaseParam: any) => {
      return donationCaseParam.caseInfo.events.find(
        (caseEvent: CaseEvent) => caseEvent.type === CaseEventType.BIRTH
      );
    };

    const getFuneralEvent = (donationCaseParam: any) => {
      return donationCaseParam.caseInfo.events.find(
        (caseEvent: CaseEvent) => caseEvent.type === CaseEventType.FUNERAL
      );
    };

    return {
      form,
      valid,
      rules,
      t,
      skipStep,
      donationCases,
      donationCase,
      steps,
      donation,
      currentStep,
      requestSession,
      isMemoryGift: isMemoryGiftComputed,
      handleDonationCasesFromRequestSession,
      selectKey,
      previousStep,
      next,
      getBirthEvent,
      getFuneralEvent,
    };
  },
});
</script>

<style scoped>
/*
 Overriding `top` styling here to align the placeholder. 
 No idea why this is needed but removing it breaks placeholder position.
 */
.compact-select :deep(.v-field__input > input) {
  top: 50%;
  transform: translateY(-50%);
}

.caseview-subtitle {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  color: #1f2a37;
  font-size: 20px;
}

.case-item-subtitle {
  font-family: 'Lato', sans-serif !important;
  font-weight: 400;
  color: #1F2A37 !important;
  font-size: 14px;
  padding-top: 10px;
  opacity: 1;
}

.case-item-name {
  font-weight: 700;
}

</style>
